import React from 'react';

import { VideoPreview } from '../../../components/VideoPreview';

interface FaceGuideProps {
  progress: number;
  stream?: MediaStream | null;
  compact?: boolean;
  mode?: 'aspect' | 'fill';
  instructionText?: string;
  instructionContainerClassName?: string;
  instructionBadgeClassName?: string;
}

export const FaceGuide: React.FC<FaceGuideProps> = ({
  progress,
  stream,
  compact = false,
  mode = 'aspect',
  instructionText = 'Posicione o rosto dentro do oval',
  instructionContainerClassName = 'top-10',
  instructionBadgeClassName = '',
}) => {
  let borderColor = '#ef4444';
  if (progress >= 0.33 && progress < 0.67) {
    borderColor = '#f59e0b';
  } else if (progress >= 0.67) {
    borderColor = '#10b981';
  }
  const aspectClass = compact ? 'aspect-[4/5]' : 'aspect-[5/6] sm:aspect-[3/4]';
  const frameClass =
    mode === 'fill'
      ? 'relative z-10 h-full w-full overflow-hidden bg-slate-950'
      : `relative z-10 w-full ${aspectClass} overflow-hidden bg-slate-950`;

  return (
    <div
      className={`relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-slate-950/70 p-0 shadow-[0_18px_50px_rgba(2,6,23,0.45)] ${
        mode === 'fill' ? 'h-full' : ''
      }`}
    >
      <div className={frameClass}>
        {stream ? (
          <VideoPreview
            stream={stream}
            width="100%"
            height="100%"
            className="h-full w-full rounded-none border-0 opacity-60"
            videoClassName="scale-[1.04] saturate-[0.9] object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-400">
            Aguardando a imagem da webcam para iniciar o guia visual.
          </div>
        )}

        <div className="pointer-events-none absolute inset-0 bg-slate-950/35" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-slate-950/45 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-950/55 to-transparent" />
      </div>

      <svg
        viewBox="0 0 240 320"
        className="pointer-events-none absolute inset-0 z-20 h-full w-full p-3"
        preserveAspectRatio="xMidYMid meet"
      >
        <ellipse
          cx="120"
          cy="160"
          rx="80"
          ry="120"
          fill="none"
          stroke={borderColor}
          strokeWidth="3"
          style={{ transition: 'stroke 0.3s' }}
        />

        <ellipse
          cx="120"
          cy="160"
          rx="80"
          ry="120"
          fill={borderColor}
          opacity={Math.min(progress * 0.3, 0.3)}
          style={{ transition: 'opacity 0.2s' }}
        />
      </svg>

      <div
        className={`pointer-events-none absolute inset-x-0 z-20 flex justify-center ${instructionContainerClassName}`}
      >
        <p
          className={`rounded-full border border-white/10 bg-slate-950/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-200 ${instructionBadgeClassName}`}
        >
          {instructionText}
        </p>
      </div>

      <div className="absolute inset-x-0 bottom-3 z-20 flex justify-center">
        <p className="rounded-full border border-white/10 bg-slate-950/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-white">
          {Math.round(progress * 100)}%
        </p>
      </div>
    </div>
  );
};
